// Auth hooks using Supabase Auth
// Replaces use-clerk-auth.ts with the same interface

import { useEffect, useState, useCallback } from 'react'
import { useAuthContext } from '../../contexts/auth-context'
import { supabase } from '../supabase-auth'
import type { Database } from '../../../database.types'

// Re-export types from supabase-auth for backwards compatibility
export type { AuthUser, AuthSession } from '../supabase-auth'

/**
 * Main auth hook - provides session, user, loading state, and signOut
 * Maintains the same interface as the previous Clerk-based implementation
 */
export function useAuth() {
  const { session, user, loading, signOut, isLoaded, isSignedIn } = useAuthContext()

  return {
    session,
    user,
    loading: !isLoaded || loading,
    signOut,
    isSignedIn,
    isLoaded,
  }
}

/**
 * Hook to get the current user's profile from the database
 * Fetches additional user data including roles
 */
export function useUserProfile() {
  const { user, isSignedIn, isLoaded } = useAuthContext()
  const [profile, setProfile] = useState<Database['public']['Tables']['users']['Row'] & { roles?: { id: string; name: string; description: string | null } | null } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isLoaded) {
        return
      }

      if (!isSignedIn || !user) {
        setProfile(null)
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select(`
            *,
            roles (
              id,
              name,
              description
            )
          `)
          .eq('id', user.id)
          .single()

        if (error) {
          console.error('Error fetching user profile:', error)
          setProfile(null)
        } else {
          setProfile(data)
        }
      } catch (error) {
        console.error('Error in fetchProfile:', error)
        setProfile(null)
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user, isSignedIn, isLoaded])

  return { profile, loading }
}

/**
 * Hook to get the Supabase client
 * The client is automatically authenticated when user is signed in
 */
export function useSupabaseClient() {
  const { supabaseClient } = useAuthContext()
  return supabaseClient
}

/**
 * Hook to get the current access token
 * Useful for making authenticated API calls to external services
 */
export function useAccessToken() {
  const { getAccessToken } = useAuthContext()
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const accessToken = await getAccessToken()
        setToken(accessToken)
      } catch (error) {
        console.error('Error getting access token:', error)
        setToken(null)
      } finally {
        setLoading(false)
      }
    }

    fetchToken()
  }, [getAccessToken])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const accessToken = await getAccessToken()
      setToken(accessToken)
    } catch (error) {
      console.error('Error refreshing access token:', error)
      setToken(null)
    } finally {
      setLoading(false)
    }
  }, [getAccessToken])

  return { token, loading, refresh }
}

/**
 * Hook to get the getToken function (for backward compatibility with Clerk-based code)
 * Returns a getToken function that can be passed to createClerkSupabaseClient
 */
export function useGetToken() {
  const { getAccessToken } = useAuthContext()

  // Return a function compatible with the old getToken interface
  return async () => {
    return await getAccessToken()
  }
}

/**
 * Get the authenticated Supabase client
 * For use outside of React components
 * @deprecated Use useSupabaseClient hook instead when possible
 */
export const getAuthenticatedSupabaseClient = () => supabase
